import React, { useLayoutEffect, useRef, useState } from 'react'
import cn from 'classnames'

import 'components/graph/controls/contextualMenu.less'

type ContextualMenuProps = {
  open: boolean
  onCollapse: () => void
  onWidthChange?: ((width: number) => void) | undefined
  children: React.ReactNode
}

const SWIPE_COLLAPSE_THRESHOLD = 40

// Generic slide-in footer panel: measures its own width, animates in/out, and collapses on
// overlay click or swipe. Its toggle button lives separately (see ContextualMenuToggle) so
// several menus' toggles can be laid out together with flexbox instead of positioned individually.
const ContextualMenu = ({
  open,
  onCollapse,
  onWidthChange,
  children,
}: ContextualMenuProps) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef<number | null>(null)
  const [panelWidth, setPanelWidth] = useState(0)

  // Re-measure on every render since which controls are visible changes with panel content.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (panelRef.current) {
      const width = panelRef.current.scrollWidth
      setPanelWidth(width)
      onWidthChange?.(width)
    }
  })

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0]?.clientX ?? null
  }

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const startX = touchStartX.current
    touchStartX.current = null
    if (startX == null) return
    const endX = e.changedTouches[0]?.clientX
    if (endX == null) return
    if (endX - startX > SWIPE_COLLAPSE_THRESHOLD) {
      onCollapse()
    }
  }

  return (
    <div className={cn('contextual-menu', { open })}>
      <div
        className={cn('contextual-menu-clip', { open })}
        style={{ width: open ? panelWidth : 0 }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="contextual-menu-panel" ref={panelRef}>
          {children}
        </div>
      </div>
    </div>
  )
}

export default ContextualMenu

